import { useCallback, useState } from 'react';
import { NativeSyntheticEvent, TextInputContentSizeChangeEventData, ViewStyle } from 'react-native';

interface UseAutoExpandTextInputOptions {
    /** Minimum visible rows at rest. Required (with `maxRows`) to activate auto-expand. */
    minRows?: number;
    /** Maximum visible rows before scrolling. Must be >= `minRows`. */
    maxRows?: number;
    /** Line height (px) of the rendered text. Must match the TextInput's `lineHeight`. */
    lineHeight: number;
    /** Combined top + bottom padding (px) of the TextInput. */
    verticalPadding: number;
    /** Combined top + bottom border width (px) of the container. */
    verticalBorderWidth: number;
}

interface UseAutoExpandTextInputResult {
    /** True when auto-expand is driving the height. Consumers fall back to a fixed height when false. */
    isActive: boolean;
    /** True when the input has expanded to its maximum. Use for web scrollbar toggling. */
    isAtMaximum: boolean;
    /** Apply to the container wrapping the TextInput. `undefined` when inactive. */
    containerStyle: ViewStyle | undefined;
    /** Wire to TextInput's `onContentSizeChange`. No-op when inactive. */
    onContentSizeChange: (event: NativeSyntheticEvent<TextInputContentSizeChangeEventData>) => void;
}

const noop = () => {};

const INACTIVE_RESULT: UseAutoExpandTextInputResult = {
    isActive: false,
    isAtMaximum: false,
    containerStyle: undefined,
    onContentSizeChange: noop,
};

/**
 * Drives the height of a multiline `TextInput`: starts at `minRows`, expands
 * with the content, and caps at `maxRows` (after which the TextInput's
 * built-in scrolling takes over).
 *
 * Activates only when both `minRows` and `maxRows` are provided and
 * `maxRows >= minRows`. Otherwise the hook is inactive and the consumer
 * should render its default fixed height.
 */
export function useAutoExpandTextInput({
    minRows,
    maxRows,
    lineHeight,
    verticalPadding,
    verticalBorderWidth,
}: UseAutoExpandTextInputOptions): UseAutoExpandTextInputResult {
    const isActive = minRows !== undefined && maxRows !== undefined && maxRows >= minRows;
    const [lastReportedHeight, setLastReportedHeight] = useState(0);

    const onContentSizeChange = useCallback((event: NativeSyntheticEvent<TextInputContentSizeChangeEventData>) => {
        setLastReportedHeight(event.nativeEvent.contentSize.height);
    }, []);

    if (!isActive) {
        return INACTIVE_RESULT;
    }

    const minimumHeight = minRows * lineHeight + verticalPadding + verticalBorderWidth;
    const maximumHeight = maxRows * lineHeight + verticalPadding + verticalBorderWidth;
    const height = Math.min(Math.max(lastReportedHeight, minimumHeight), maximumHeight);

    const isAtMaximum = height >= maximumHeight;
    const containerStyle: ViewStyle = { height };

    return {
        isActive,
        isAtMaximum,
        containerStyle,
        onContentSizeChange,
    };
}
